import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
} from 'reactstrap';
import Select from 'react-select';
import { FormType } from 'components/Modals/formTypes';
import { GenericFormModal } from 'components/Modals/AddGenericModal';
import { customSelectStyles } from 'components/Common/SelectStyle';
import { useCreateMutation, useListQuery } from 'helpers/http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'helpers/http-client/httpConstants';
import Breadcrumb from 'components/Common/Breadcrumb';
import { useNavigate } from 'react-router-dom';

interface InvoiceData {
  invoiceNumber: string;
  date: string;
  supplierId: string | null;
  manufacturerId: string | null;
}

interface ItemRow {
  medicineId: string;
  batchNumber: string;
  packing?: string;
  quantity: number | string;
  bonusQuantity?: number | string;
  rate: number | string;
  discountPct?: number | string;
  discountValue?: number | string;
  taxAmount?: number | string;
  netAmount?: number | string;
  netValue: number | string;
  expiryDate?: string;
  piecesInPacking?: number | string;
  salesPricePerUnit?: number | string;
  purchasePricePerUnit?: number | string;
  margin?: number | string;
  _salesPriceAuto?: boolean;
  _marginAuto?: boolean;
}

const PurchaseInvoiceEntry: React.FC = () => {
  const navigate = useNavigate();
  const [modalFormType, setModalFormType] = useState<FormType | null>(null);
  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    supplierId: null,
    manufacturerId: null,
  });

  const [items, setItems] = useState<ItemRow[]>([]);

  const {
    data: supplierOptions,
    isLoading: isLoadingSuppliers,
    isError: isErrorSuppliers,
  } = useListQuery(API_ENDPOINTS.SUPPLIERS);

  const {
    data: manufacturerOptions,
    isLoading: isLoadingManufacturers,
    isError: isErrorManufacturers,
  } = useListQuery(API_ENDPOINTS.MANUFACTURERS);

  const {
    data: fetchedMedicines = [],
    isLoading: isLoadingMedicines,
    isError: isErrorMedicines,
  } = useListQuery(API_ENDPOINTS.MEDICINES);

  const medicineSelectOptions = fetchedMedicines.map((med: any) => ({
    label: med.name,
    value: med.id,
  }));

  useEffect(() => {
    if (items.length === 0) addNewRow();
  }, []);

  const handleChange = (field: keyof InvoiceData, value: any) => {
    setInvoiceData((prev) => ({ ...prev, [field]: value }));
  };

  const addNewRow = () => {
    setItems((prev) => [
      ...prev,
      {
        medicineId: '',
        batchNumber: '',
        packing: 'Box of 10 tablets',
        quantity: '',
        bonusQuantity: '',
        rate: '',
        discountPct: '',
        discountValue: '',
        taxAmount: '',
        netValue: '',
        expiryDate: '',
        piecesInPacking: '',
        purchasePricePerUnit: '',
        salesPricePerUnit: '',
        margin: 50,
        _salesPriceAuto: true,
        _marginAuto: true,
      },
    ]);
  };

  const removeRow = (index: number) => {
    if (items.length > 1) {
      const updated = [...items];
      updated.splice(index, 1);
      setItems(updated);
    }
  };

  const handleItemChange = (index: number, field: keyof ItemRow, value: any) => {
    const updatedItems = [...items];
    const item = { ...updatedItems[index], [field]: value };

    const quantity = Number(item.quantity) || 0;
    const rate = Number(item.rate) || 0;
    const totalPrice = quantity * rate;

    if (field === 'discountPct') {
      item.discountPct = value;
      item.discountValue = (totalPrice * value) / 100;
    } else if (field === 'discountValue') {
      item.discountValue = value;
      const discountPct = totalPrice > 0 ? (value / totalPrice) * 100 : 0;
      item.discountPct = discountPct.toFixed(2);
    }

    const netValueBeforeTax = totalPrice - (Number(item.discountValue) || 0);
    const tax = Number(item.taxAmount) || 0;
    item.netValue = netValueBeforeTax + tax;

    const piecesInPacking = Number(item.piecesInPacking) || 0;
    const totalUnits = quantity * piecesInPacking;
    item.purchasePricePerUnit = totalUnits > 0 ? netValueBeforeTax / totalUnits : 0;

    const margin = Number(item.margin) ?? 0;
    const salesPricePerUnit = Number(item.salesPricePerUnit) ?? 0;

    if (field === 'margin') {
      item.salesPricePerUnit = parseFloat(
        (item.purchasePricePerUnit * (1 + margin / 100)).toFixed(2),
      );
      item._marginAuto = true;
      item._salesPriceAuto = false;
    } else if (field === 'salesPricePerUnit') {
      item.margin =
        item.purchasePricePerUnit > 0
          ? parseFloat(
              (
                ((salesPricePerUnit - item.purchasePricePerUnit) / item.purchasePricePerUnit) *
                100
              ).toFixed(2),
            )
          : 0;
      item._marginAuto = false;
      item._salesPriceAuto = true;
    } else if (item._salesPriceAuto !== false) {
      item.margin = 50;
      item.salesPricePerUnit = parseFloat((item.purchasePricePerUnit * 1.5).toFixed(2));
      item._salesPriceAuto = true;
      item._marginAuto = true;
    }

    updatedItems[index] = item;
    setItems(updatedItems);
  };

  const { mutate: createPurchaseInvoice } = useCreateMutation(API_ENDPOINTS.PURCHASE);
  const handleSave = async () => {
    const totalAmount = items.reduce((sum, i) => sum + (+i.netValue || 0), 0);

    const payload = {
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: invoiceData.date,
      supplierId: invoiceData.supplierId,
      bookedBy: 'Admin',
      deliveredBy: '',
      totalAmount,
      items: items.map((item) => ({
        medicineId: item.medicineId,
        batchNumber: item.batchNumber,
        quantity: item.quantity,
        bonusQuantity: item.bonusQuantity,
        rate: item.rate,
        discountPct: item.discountPct,
        discountValue: item.discountValue,
        taxAmount: item.taxAmount,
        netValue: item.netValue,
        expiryDate: item.expiryDate || null,
        packing: item.packing,
        piecesInPacking: item.piecesInPacking,
        purchasePricePerUnit: item.purchasePricePerUnit,
        salesPricePerUnit: item.salesPricePerUnit,
        margin: item.margin,
        manufacturerId: invoiceData.manufacturerId,
      })),
    };
    navigate('/purchases/invoices/confirmation', { state: { invoiceData: payload } });
    try {
      createPurchaseInvoice(payload);
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Error saving invoice. See console for details.');
    }
  };

  const handleModalSuccess = (newEntity: any) => {
    const newOption = { label: newEntity.name, value: newEntity.id };
    if (modalFormType === FormType.SUPPLIER) {
      setInvoiceData((prev) => ({ ...prev, supplierId: newOption.value }));
    } else if (modalFormType === FormType.MANUFACTURER) {
      setInvoiceData((prev) => ({ ...prev, manufacturerId: newOption.value }));
    }
    setModalFormType(null);
  };

  const totalPurchasedItems = items?.length;
  const totalNetAmount = items?.reduce((sum, item) => sum + (+item?.netValue || 0), 0);
  const totalTax = items.reduce((sum, item) => sum + (Number(item?.taxAmount) || 0), 0);

  return (
    <div className="page-content">
      <Container fluid className="mt-4">
        <Breadcrumb title="Inventory" breadcrumbItem="Purchase Invoices Entry" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Form>
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Invoice Number</Label>
                        <Input
                          type="text"
                          value={invoiceData.invoiceNumber}
                          onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                          placeholder="e.g. INV-00123"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={invoiceData.date}
                          onChange={(e) => handleChange('date', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Supplier</Label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <div style={{ flexGrow: 1 }}>
                            <Select
                              value={supplierOptions?.find(
                                (opt: any) => opt.value === invoiceData.supplierId,
                              )}
                              onChange={(option) =>
                                handleChange('supplierId', option?.value || null)
                              }
                              options={supplierOptions?.map((opt) => ({
                                label: opt.name,
                                value: opt.id,
                              }))}
                              isLoading={isLoadingSuppliers}
                              placeholder="Select Supplier"
                            />
                          </div>
                          <Button
                            color="success"
                            onClick={() => setModalFormType(FormType.SUPPLIER)}
                          >
                            +
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Manufacturer</Label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <div style={{ flexGrow: 1 }}>
                            <Select
                              value={manufacturerOptions?.find(
                                (opt: any) => opt.value === invoiceData.manufacturerId,
                              )}
                              onChange={(option) =>
                                handleChange('manufacturerId', option?.value || null)
                              }
                              options={manufacturerOptions?.map((opt) => ({
                                label: opt.name,
                                value: opt.id,
                              }))}
                              isLoading={isLoadingManufacturers}
                              placeholder="Select Manufacturer"
                            />
                          </div>
                          <Button
                            color="success"
                            onClick={() => setModalFormType(FormType.MANUFACTURER)}
                          >
                            +
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
                <h5 className="mb-3">Items</h5>
                <Row className="align-items-center mb-3">
                  <Col xs="6">
                    <Button color="secondary" onClick={addNewRow}>
                      + Add Item
                    </Button>
                  </Col>
                  <Col xs="6" className="text-end">
                    <Button color="success" onClick={() => setModalFormType(FormType.MEDICINE)}>
                      + Add Medicine
                    </Button>
                  </Col>
                </Row>

                <div
                  style={{
                    overflowX: 'auto',
                    maxHeight: '500px',
                    minHeight: '200px',
                  }}
                >
                  <Table bordered responsive size="sm" className="table-sticky">
                    <thead
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: '#f7f7f7',
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th className="text-nowrap" style={{ minWidth: '250px' }}>
                          Medicine
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Batch No.
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '120px' }}>
                          Packing
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Pieces per Pack
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Qty (Packs)
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Bonus Qty
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Rate
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Discount %
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Discount Value
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Tax Value
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Net Value
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Margin %
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '100px' }}>
                          Sales Price/Unit
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '120px' }}>
                          Expiry Date
                        </th>
                        <th className="text-nowrap" style={{ minWidth: '80px' }}>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index} style={{ backgroundColor: '#fff' }}>
                          <td style={{ padding: 0, height: '30px' }}>
                            <Select
                              value={
                                medicineSelectOptions.find(
                                  (opt) => opt.value === item.medicineId,
                                ) || null
                              }
                              onChange={(selected) =>
                                handleItemChange(index, 'medicineId', selected?.value || '')
                              }
                              options={medicineSelectOptions}
                              isLoading={isLoadingMedicines}
                              placeholder="Select Medicine"
                              styles={customSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              value={item.batchNumber}
                              onChange={(e) =>
                                handleItemChange(index, 'batchNumber', e.target.value)
                              }
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              value={item.packing}
                              onChange={(e) => handleItemChange(index, 'packing', e.target.value)}
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.piecesInPacking}
                              onChange={(e) =>
                                handleItemChange(index, 'piecesInPacking', +e.target.value)
                              }
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', +e.target.value)}
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.bonusQuantity}
                              onChange={(e) =>
                                handleItemChange(index, 'bonusQuantity', +e.target.value)
                              }
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', +e.target.value)}
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              max={100}
                              value={item.discountPct}
                              onChange={(e) =>
                                handleItemChange(index, 'discountPct', +e.target.value)
                              }
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              onChange={(e) =>
                                handleItemChange(index, 'discountValue', +e.target.value)
                              }
                              min={0}
                              value={item.discountValue}
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.taxAmount}
                              onChange={(e) =>
                                handleItemChange(index, 'taxAmount', +e.target.value)
                              }
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.netValue}
                              readOnly
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.margin}
                              onChange={(e) => handleItemChange(index, 'margin', +e.target.value)}
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="number"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              min={0}
                              value={item.salesPricePerUnit}
                              onChange={(e) =>
                                handleItemChange(index, 'salesPricePerUnit', +e.target.value)
                              }
                              bsSize="sm"
                            />
                          </td>
                          <td style={{ padding: 0 }}>
                            <Input
                              type="date"
                              style={{
                                border: 'none',
                                height: '100%',
                                width: '100%',
                                padding: '0 4px',
                                fontSize: '0.875rem',
                              }}
                              value={item.expiryDate}
                              onChange={(e) =>
                                handleItemChange(index, 'expiryDate', e.target.value)
                              }
                              bsSize="sm"
                            />
                          </td>
                          <td className="text-center" style={{ padding: '2px' }}>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => removeRow(index)}
                              disabled={items.length <= 1}
                              style={{ minWidth: '40px' }}
                            >
                              ✕
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <Row className="mt-3 justify-content-end">
                  <Col xs="12" sm="8" md="6" lg="4">
                    <div>
                      <CardBody className="py-2 px-3">
                        <div className="d-flex justify-content-between mb-2">
                          <strong>Total Purchased Items:</strong>
                          <span>{totalPurchasedItems}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <strong>Total Net Amount:</strong>
                          <span>{totalNetAmount.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <strong>Total Tax:</strong>
                          <span>{totalTax.toFixed(2)}</span>
                        </div>
                      </CardBody>
                    </div>
                  </Col>
                </Row>

                <div className="mt-4 text-end">
                  <Button color="primary" onClick={handleSave}>
                    Save Invoice
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <GenericFormModal
          isOpen={modalFormType !== null}
          toggle={() => setModalFormType(null)}
          formType={modalFormType as FormType}
          onSuccess={handleModalSuccess}
        />
      </Container>
    </div>
  );
};

export default PurchaseInvoiceEntry;
